import { ExpandedState, SortingState } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useCallback, useMemo, useState } from 'react';
import {
  SalesAggregate,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext/useCatalogDataContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { NoData } from 'src/tables/Table';
import { GroupedVirtuosoTable } from 'src/tables/TableV2/GroupedVirtuosoTable';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getEventPerformerVenue,
  getFormattedEventName,
} from 'src/utils/eventWithDataUtils';
import {
  Feature,
  FlightPacingOption,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
  UiCurrency,
} from 'src/WebApiController';

import { CampaignRowForm } from '../CampaignRowForm/CampaignRowForm';
import * as styles from '../SponsoredListingsTable.css';
import { getTableColumnDefinitions } from './CampaignFlightsTableColumnsConfig';
import { useCampaignFlightsTableMetricsData } from './useCampaignFlightsTableMetricsData';

export type CampaignFlightWithCampaign = SponsoredListingsFlight & {
  campaign: SponsoredListingsCampaign;
};

export type FlightTableRow = {
  id: string;
  flightId: number;
  campaignId: number;
  name: string;
  eventId?: number;
  eventName?: string;
  eventNameParts?: string[];
  eventDate?: Date;
  currency?: UiCurrency;
  salesData?: SalesAggregate; // Flight-specific sales data
  hasChildren: boolean;
  campaign?: SponsoredListingsCampaign;
  flight?: SponsoredListingsFlight;
  duration: number; // Duration in hours
  groupDate?: string; // For date grouping
  flights?: FlightTableRow[]; // For grouped flights
};

export type CampaignFlightsTableProps = {
  isLoading: boolean;
  disabled?: boolean;
  campaignsAndFlights:
    | [SponsoredListingsCampaign, SponsoredListingsFlight][]
    | null
    | undefined;
  onStartWork?: () => void;
  onStopWork?: (success: boolean) => void;
  showEventColumns?: boolean;
  groupByDate?: boolean;
};

export const CampaignFlightsTable: React.FC<CampaignFlightsTableProps> = ({
  isLoading,
  campaignsAndFlights,
  onStartWork,
  onStopWork,
  disabled,
  showEventColumns = true,
  groupByDate = false,
}) => {
  const { data } = useCatalogDataContext();
  const { getUiCurrency } = useLocalizationContext();
  const { campaignSalesAggregates } = useAdCampaignsDataContext();
  const hasReportingMetrics = useUserHasFeature(
    Feature.SponsoredListingsV2_ReportingMetrics
  );

  // Fetch and merge reporting metrics with campaign flights data
  const campaignsAndFlightsWithMetrics =
    useCampaignFlightsTableMetricsData(campaignsAndFlights);

  const columns = useMemo(() => {
    let columns = getTableColumnDefinitions(onStartWork, onStopWork, disabled);
    if (!showEventColumns) {
      columns = columns.filter(
        (column) => column.id !== 'eventName' && column.id !== 'eventDate'
      );
    }
    if (!hasReportingMetrics) {
      columns = columns.filter(
        (column) =>
          column.id !== 'historic-potential-impressions' &&
          column.id !== 'actual-impressions' &&
          column.id !== 'impression-share' &&
          column.id !== 'clicks' &&
          column.id !== 'potential-sales' &&
          column.id !== 'potential-revenue'
      );
    }
    return columns;
  }, [
    onStartWork,
    onStopWork,
    disabled,
    showEventColumns,
    hasReportingMetrics,
  ]);

  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const [sortState, setSortState] = useState<SortingState>([
    { id: 'startDate', desc: true },
  ]);

  const calculateDuration = useCallback(
    (flight: SponsoredListingsFlight): number => {
      if (!flight.dateRange.start || !flight.dateRange.end) {
        return 0;
      }
      const startDate = new Date(flight.dateRange.start!);
      const endDate = new Date(flight.dateRange.end!);
      return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60); // Duration in hours
    },
    []
  );

  const getSubRows = useCallback((data: FlightTableRow) => {
    if (data.groupDate && data.hasChildren) {
      // This is a date group row, return its flights
      return data.flights || [];
    }
    return [];
  }, []);

  const tableData = useMemo<FlightTableRow[]>(() => {
    if (!campaignsAndFlightsWithMetrics) return [];

    const flightRows: FlightTableRow[] = campaignsAndFlightsWithMetrics.map(
      ([campaign, flight]) => {
        const { event, venue, performer } = getEventPerformerVenue(
          campaign.eventId.toString(),
          data
        );

        const [eventName, eventNameConnector, eventSubName] =
          getFormattedEventName({
            event: event?.event,
            performer,
            venue,
            slimMode: false,
          });

        const campaignSalesData =
          campaignSalesAggregates?.[campaign.campaignId];
        const flightSalesData =
          campaignSalesData?.flightsAggregate?.[flight.flightId];
        const currency = getUiCurrency(
          campaignSalesData?.currencyCode || campaign.currencyCode || 'USD'
        );

        const duration = calculateDuration(flight);

        return {
          id: flight.flightId.toString(),
          flightId: flight.flightId,
          campaignId: campaign.campaignId,
          name: campaign.campaignName,
          eventId: campaign.eventId,
          eventName: `${eventName}${eventNameConnector}${eventSubName}`,
          eventNameParts: [eventName, eventNameConnector, eventSubName],
          eventDate: event?.event?.dates.start
            ? new Date(event.event.dates.start)
            : undefined,
          currency: currency,
          salesData: flightSalesData,
          hasChildren: false,
          campaign: campaign,
          flight: flight,
          duration: duration,
        } as FlightTableRow;
      }
    );

    if (!groupByDate) {
      return flightRows.toSorted((a, b) => {
        if (!a.flight?.dateRange?.start || !b.flight?.dateRange?.start) {
          return 0;
        }
        const aDate = new Date(a.flight.dateRange.start!).getTime();
        const bDate = new Date(b.flight.dateRange.start!).getTime();
        return bDate - aDate;
      });
    }

    // Group by date
    const groupedByDate = flightRows.reduce(
      (groups, flight) => {
        if (!flight.flight?.dateRange?.start) {
          return groups;
        }
        const dateValue = new Date(
          flight.flight.dateRange.start!
        ).toISOString();
        const dateKey = formatInTimeZone(
          new Date(dateValue),
          'UTC',
          'MM/dd/yyyy'
        );

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(flight);
        return groups;
      },
      {} as Record<string, FlightTableRow[]>
    );

    const result: FlightTableRow[] = [];

    Object.keys(groupedByDate)
      .toSorted((a, b) => b.localeCompare(a)) // Sort dates descending
      .forEach((dateKey) => {
        const flights = groupedByDate[dateKey];
        const firstFlight = flights[0];

        // Create group header row
        const groupRow: FlightTableRow = {
          id: `date-${dateKey}`,
          flightId: 0,
          campaignId: 0,
          name: dateKey,
          hasChildren: true,
          duration: 0,
          groupDate: dateKey,
          flights: flights,
          currency: firstFlight.currency,
        };

        result.push(groupRow);
      });

    return result;
  }, [
    campaignsAndFlightsWithMetrics,
    data,
    campaignSalesAggregates,
    getUiCurrency,
    calculateDuration,
    groupByDate,
  ]);

  const onRowWrapper = useCallback(
    (row: FlightTableRow, tableRow: React.ReactNode): React.ReactNode => {
      if (!groupByDate || !row.hasChildren) {
        return row.campaign && row.flight?.isCurrent ? (
          <CampaignRowForm campaign={row.campaign}>{tableRow}</CampaignRowForm>
        ) : (
          tableRow
        );
      }

      return (
        <div
          className={styles.rowWrapper}
          onClick={() =>
            setExpandedState((prev: ExpandedState) => {
              const record = prev as Record<string, boolean>;
              record[row.id.toString()] = !record[row.id.toString()];
              return { ...record };
            })
          }
        >
          {row.campaign && row.flight?.isCurrent ? (
            <CampaignRowForm campaign={row.campaign}>
              {tableRow}
            </CampaignRowForm>
          ) : (
            tableRow
          )}
        </div>
      );
    },
    [groupByDate]
  );

  const tableMeta = useMemo(() => {
    const flightsSums = campaignsAndFlightsWithMetrics?.reduce(
      (sums, candf) => {
        const c = candf[0];
        const f = candf[1];
        const salesData = campaignSalesAggregates?.[c.campaignId];
        const flightSalesData = salesData?.flightsAggregate?.[f.flightId];

        sums.currencyCode = c.currencyCode || sums.currencyCode;
        sums.totalBudget += f.flightBudget ?? 0;
        sums.totalSpend += f.flightSpend ?? 0;
        sums.totalSales += f.salesCount ?? flightSalesData?.saleCount ?? 0;
        sums.totalCost += flightSalesData?.totalCost ?? 0;
        sums.totalNetProceeds +=
          f.salesProceeds ?? flightSalesData?.totalNetProceeds ?? 0;
        sums.totalHistoricPotentialImpressions +=
          f.historicPotentialImpressions ?? 0;
        sums.totalActualImpressions += f.actualImpressions ?? 0;
        sums.totalClicks += f.clicks ?? 0;
        sums.totalSalesCount += f.salesCount ?? 0;
        sums.totalSalesProceeds += f.salesProceeds ?? 0;
        sums.totalPotentialSales += f.potentialSales ?? 0;
        sums.totalPotentialRevenue += f.potentialRevenue ?? 0;

        return sums;
      },
      {
        currencyCode: 'USD',
        totalBudget: 0,
        totalSpend: 0,
        totalSales: 0,
        totalCost: 0,
        totalNetProceeds: 0,
        totalHistoricPotentialImpressions: 0,
        totalActualImpressions: 0,
        totalClicks: 0,
        totalSalesCount: 0,
        totalSalesProceeds: 0,
        totalPotentialSales: 0,
        totalPotentialRevenue: 0,
      }
    );

    const currency = getUiCurrency(flightsSums?.currencyCode || 'USD');

    const totalHistoricPotentialImpressions =
      flightsSums?.totalHistoricPotentialImpressions ?? 0;
    const totalActualImpressions = flightsSums?.totalActualImpressions ?? 0;
    const totalImpressionShare =
      totalHistoricPotentialImpressions > 0
        ? (totalActualImpressions / totalHistoricPotentialImpressions) * 100
        : null;

    return {
      summaryData: {
        id: 'summary-total',
        flightId: 0,
        campaignId: 0,
        hasChildren: false,
        duration: 0,
        name: 'Total',
        currency: currency,
        flight: {
          flightId: 0,
          campaignId: 0,
          isCurrent: true,
          dateRange: { start: '', end: '' },
          baseBid: 0,
          maxBid: 0,
          pacingOption: FlightPacingOption.ASAP,
          flightBudget: flightsSums?.totalBudget,
          flightSpend: flightsSums?.totalSpend,
          totalSpend: flightsSums?.totalSpend,
          currencyCode: flightsSums?.currencyCode || 'USD',
          historicPotentialImpressions: totalHistoricPotentialImpressions,
          actualImpressions: totalActualImpressions,
          impressionShare: totalImpressionShare,
          clicks: flightsSums?.totalClicks ?? 0,
          salesCount: flightsSums?.totalSalesCount ?? 0,
          salesProceeds: flightsSums?.totalSalesProceeds ?? 0,
          potentialSales: flightsSums?.totalPotentialSales ?? 0,
          potentialRevenue: flightsSums?.totalPotentialRevenue ?? 0,
        },
        salesData: {
          totalCost: flightsSums?.totalCost,
          totalNetProceeds: flightsSums?.totalNetProceeds,
          saleCount: flightsSums?.totalSales,
          currencyCode: flightsSums?.currencyCode || 'USD',
        } as SalesAggregate,
      } as FlightTableRow,
    };
  }, [campaignSalesAggregates, campaignsAndFlightsWithMetrics, getUiCurrency]);

  if (isLoading) {
    return <PosSpinner />;
  }

  return campaignsAndFlights?.length ? (
    <ColumnResizingContextProvider>
      {groupByDate ? (
        <GroupedVirtuosoTable<FlightTableRow>
          style={{ height: '100%' }}
          actionColId={'actions'}
          data={tableData}
          columns={columns}
          onExpandedChange={setExpandedState}
          onSortingChange={setSortState}
          getRowId={(data) => String(data.id)}
          getGroupId={(data) => data.groupDate || data.id}
          getSubRows={getSubRows}
          getItemId={(data) => data.id}
          renderGroupAsNormalRow
          isGroupRow={(data) => data.hasChildren}
          rowWrapper={onRowWrapper}
          state={{
            sorting: sortState,
            expanded: expandedState,
          }}
        />
      ) : (
        <GroupedVirtuosoTable<FlightTableRow>
          style={{ height: '100%' }}
          actionColId={'actions'}
          data={tableData}
          columns={columns}
          getRowId={(data) => String(data.id)}
          getItemId={(data) => data.id}
          onSortingChange={setSortState}
          rowWrapper={onRowWrapper}
          meta={tableMeta}
          state={{
            sorting: sortState,
          }}
        />
      )}
    </ColumnResizingContextProvider>
  ) : (
    <NoData style={{ border: `1px solid ${vars.color.borderPrimary}` }}>
      <Content id={ContentId.None} />
    </NoData>
  );
};
